import { Input, Output, OnInit, AfterViewInit, ElementRef, ViewChild, EventEmitter, Directive } from '@angular/core';
import { ActivatedRoute, Router, ParamMap, Params } from '@angular/router';
import { environment } from '@env/environment';
import { DomSanitizer, SafeResourceUrl, } from '@angular/platform-browser';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';
import { map, filter, switchMap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { Course, CourseRelease, ExperienceType, Learnspace, ProgramTabs } from '@shared/models';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { LinkService } from '@shared/services/link.service';
import { CourseEditDialogComponent } from '@app/main/apps/course-edit-dialog/course-edit-dialog.component';
import { AuthenticationService } from '@shared/services/authentication.service';
import { EmbedDialogComponent } from '@shared/components/embed-dialog/embed-dialog.component';
import { LMSDialogComponent } from '@shared/components/embed-dialog/lms-dialog.component'
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { DownloadDialogComponent } from '@shared/components/download-dialog/download-dialog.component';
import { CourseAnalyticsTab } from '@shared/services/analytics.service';
import { courseLabel } from '@shared/utils/wording.utils';

@Directive()
export class CourseViewBaseComponent implements OnInit, AfterViewInit {
  @Input() course: Course;
  @Input() canCreateCourse: boolean;
  @Input() learnspaceList: Learnspace[];

  @ViewChild('actionButtons', { read: ElementRef }) actionButtons: ElementRef;
  id: string;
  paramMap: ParamMap | Map<string, string>;
  url: SafeResourceUrl;
  isDisabled: boolean;
  compatMsg: string;
  renewMsg = '';
  @Output()
  listChange = new EventEmitter<{ action: string, content: Course | string }>();

  constructor(
    protected _router: Router,
    protected _activatedRoute: ActivatedRoute,
    protected sanitizer: DomSanitizer,
    protected _currentHubService: CurrentHubService,
    protected _internalAnalyticsService: InternalAnalyticsService,
    protected _linkService: LinkService,
    protected el: ElementRef,
    protected _courseApiService: CoursesAPIService,
    protected _dialog: MatDialog,
    protected _authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.isDisabled = !this.canEnter(this.course);
    this.compatMsg = `You can’t update or launch this ${courseLabel(this.course.experience_type)}`;
    this.findCourseId().subscribe(id => {
      const multi = this.paramMap.get('multi') && this.paramMap.get('multi') === 'true';
      const presenter = this.paramMap.get('presenter') && this.paramMap.get('presenter') === 'true';
      const session = this.paramMap.get('session');
      const poster = this.paramMap.get('poster') === 'true' || !this.paramMap.get('poster');
      let unsafeURL = environment.roomUrl + '?course=' + this.id;
      if (multi) {
        unsafeURL += '&multi=true';
      }
      if (presenter) {
        unsafeURL += '&presenter=true';
      }
      if (session && session !== 'null') {
        unsafeURL += '&session=' + session;
      }
      if (poster) {
        unsafeURL += '&poster=true';
      }
      this.url = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeURL);
    });
  }

  getCourseLatestRelease(): CourseRelease {
    const isVersioned = this._currentHubService.isPaid() && !this.course.is_multi && this.course.information.versioningEnabled;
    if (!isVersioned || !this.course.courseReleases || !this.course.courseReleases.length) {
      return null;
    }
    return this.course.courseReleases[0];
  }

  ngAfterViewInit(): void {
    // skip fadeout animation at view init
    if (this.actionButtons) {
      this.actionButtons.nativeElement.addEventListener('animationend', () => {
        this.actionButtons.nativeElement.classList.remove('hidden');
      }, { once: true });
    }
  }

  findCourseId(): Observable<string> {
    if (this.course) {
      this.id = this.course.id;
      this.paramMap = new Map([['multi', 'false'], ['presenter', 'true'], ['session', 'null']]);
      return of(this.id);
    } else {
      return this._activatedRoute.params.pipe(
        map(params => {
          this.id = params['id'];
          const realId = this.id.split('?')[0];
          if (realId !== this.id) { // bug in angular decode uri but queryparams and id are not correct
            this.id = realId;
            const path = decodeURIComponent(window.location.pathname);
            this.paramMap = this._router.parseUrl(path).queryParamMap;
          } else {
            this.paramMap = this._activatedRoute.snapshot.queryParamMap;
          }

          return this.id;
        })
      );
    }
  }

  openIntercom(): void {
    this._internalAnalyticsService.showNewMessage();
  }

  goToCourse(): void {
    const editorPath = this.course.experience_type === ExperienceType.ai ? 'simulations' : 'courses';
    this._router.navigate([`./${editorPath}/${this.id}`], { relativeTo: this._activatedRoute });
  }

  goToAssessments(): void {
    if (this.course.experience_type === ExperienceType.ai) {
      const params: Params = { activeTab: ProgramTabs.Reporting, simulationId: this.course.id, section: "reports" };
      this._router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: params
      });
    } else {
      const url = `analytics/${this._currentHubService.hub.urlTitle}/${this.course.learnspace_id}/courses/${this.course.id}`;
      this._router.navigate([url], {
        queryParams: {
          'activeTab': CourseAnalyticsTab.Assessment,
        }
      });
    }
  }

  launchCourse(): void {
    this._linkService.launchCourse(this.id, this.course.is_multi);
  }

  canDelete(courseOwnerId: string): boolean {
    return courseOwnerId === this._authService.wondaUser.id || this._currentHubService.isAdminOfCurrentHub(this._authService.wondaUser);
  }

  canEnter(course: Course): boolean {
    return course.status !== 'archived';
  }

  userCanMove(): boolean {
    return this._currentHubService.isAdminOfCurrentHub(this._authService.wondaUser);
  }

  canUseLMS(): boolean {
    return this._currentHubService.plan.allowLTI;
  }

  moveEnabled(): boolean {
    return this._currentHubService.isPaid();
  }
  
  openEmbedDialog(course: Course): void {
    this._dialog
      .open(EmbedDialogComponent, {
        panelClass: ['learnspace-theme'],
        data: { course: course, styleClass: 'learnspace-theme' },
        autoFocus: false
      })
      .afterClosed()
      .subscribe(updatedCourse => {
        if (updatedCourse) {
          this.listChange.emit({ action: 'update', content: updatedCourse });
        }
      });
  }

  openLMSDialog(course: Course): void {
    this._dialog
      .open(LMSDialogComponent, {
        panelClass: ['learnspace-theme'],
        data: { course: course }
      });
  }

  openDownloadDialog(course: Course): void {
    // tslint:disable-next-line:max-line-length
    this._dialog
      .open(DownloadDialogComponent, {
        panelClass: ['learnspace-theme'],
        data: { course: course, styleClass: 'learnspace-theme' }
      });
  }

  duplicateCourse(id: string): void {
    this._courseApiService.duplicateCourse(id).subscribe(newCourse =>
      this.listChange.emit({ action: 'add', content: newCourse })
    );
  }

  updateCourse(course: Course): void {
    this._dialog
      .open(CourseEditDialogComponent, {
        height: '630px', width: '50vw',
        panelClass: ['learnspace-theme'],
        data: { course: course, childrenClass: 'learnspace-theme' }
      })
      .afterClosed()
      .subscribe(updatedCourse => {
        if (updatedCourse) {
          this.listChange.emit({ action: 'update', content: updatedCourse });
        }
      });
  }

  deleteCourse(course: Course): void {
    this._dialog
      .open(ConfirmationDialogComponent, {
        width: '400px',
        height: 'auto',
        panelClass: 'learnspace-theme',
        data: { title: course.title, type: courseLabel(course.experience_type) }
      })
      .afterClosed()
      .pipe(
        filter(val => val === 'ok'),
        switchMap(a => {
          return this._courseApiService
            .deleteCourse(course.id);
        })
      )
      .subscribe(val => {
        this.listChange.emit({ action: 'remove', content: val });
      });
  }

  moveCourse(space: Learnspace, course: Course) {
    this._courseApiService.updateCourse(course.id, { learnspace_id: space.id }).subscribe(
      course => {
        const url = `${this._currentHubService.hub.urlTitle}/${course.learnspace_id}`;
        this._router.navigate([url]);
      }
    )
  }

  openAnalytics(courseId: string): void {
    const url = `analytics/${this._router.url}/courses/${courseId}`;
    this._router.navigate([url]);
  }

  public is360Only(): boolean {
    return this.course.experience_type === ExperienceType.only360;
  }
}

